





























































































































































import {
  defineComponent, ref, PropType, computed, watch,
} from 'vue';

import api from '@/api';
import TablePanel from '@/components/TablePanel.vue';
import NetworkPanel from '@/components/NetworkPanel.vue';
import store from '@/store';
import WorkspaceOptionMenu from '@/components/WorkspaceOptionMenu.vue';
import { App } from '@/types';
import { useCurrentInstance } from '@/utils/use';

const surroundingWhitespace = /^\s+|\s+$/;
const workspaceNameRules: Array<(x: string) => string|boolean> = [
  (x: string) => !!x || 'Workspace name cannot be blank',
  (x: string) => !surroundingWhitespace.test(x) || 'Workspace name cannot begin or end with whitespace',
];

export default defineComponent({
  name: 'WorkspaceDetail',

  components: {
    TablePanel,
    NetworkPanel,
    WorkspaceOptionMenu,
  },

  props: {
    workspace: {
      type: String as PropType<string>,
      required: true,
    },
    apps: {
      type: Object as PropType<{ network_visualizations: App[]; table_visualizations: App[] }>,
      required: true,
    },
  },

  setup(props) {
    const router = useCurrentInstance().proxy.$router;

    const localWorkspace = ref<string | null>(null);
    const editing = ref(false);
    const requestError = ref<string | null>(null);
    const loading = ref(false);

    const nodeTables = computed(() => store.getters.nodeTables);
    const edgeTables = computed(() => store.getters.edgeTables);
    const tables = computed(() => store.getters.tables);
    const networks = computed(() => store.getters.networks);
    const uploads = computed(() => store.state.uploads.filter(
      (upload) => upload.status === 'PENDING' || upload.status === 'STARTED',
    ));
    const nameErrorMessages = computed(() => {
      const errors = [
        ...workspaceNameRules.map((rule) => rule(localWorkspace.value as string)),
        requestError,
      ];

      return errors.filter((res): res is string => typeof res === 'string');
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function cancelRename() {
      requestError.value = null;
      localWorkspace.value = props.workspace;
      editing.value = false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function renameWorkspace() {
      if (nameErrorMessages.value.length) {
        return;
      }

      if (localWorkspace.value === props.workspace) {
        editing.value = false;
        return;
      }

      if (localWorkspace.value !== null) {
        try {
          const { name } = await api.renameWorkspace(props.workspace, localWorkspace.value);
          router.push(`/workspaces/${name}`);
          editing.value = false;
          requestError.value = null;

          store.dispatch.fetchWorkspaces();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          if (err.response.status === 409) {
            requestError.value = 'A workspace by that name already exists';
          } else {
            requestError.value = `${Object.values(err.response.data).flat()[0]}`;
          }
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function update(this: any) {
      loading.value = true;

      localWorkspace.value = props.workspace;
      await store.dispatch.fetchWorkspace(props.workspace);
      loading.value = false;
    }

    watch(() => props.workspace, () => update());
    watch(localWorkspace, () => { requestError.value = null; });

    update();

    return {
      editing,
      loading,
      networks,
      nodeTables,
      edgeTables,
      tables,
      cancelRename,
      renameWorkspace,
      nameErrorMessages,
      localWorkspace,
      uploads,
    };
  },
});
